import { ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useMatches } from '~/router/hooks'
import { LoadingScreen } from './LoadingScreen'

interface AuthGuardProps {
  children: ReactNode
}

/**
 * Handles the authentication guard for protected routes.
 * Shared routes (isShared handle) are allowed through without auth.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const matches = useMatches()
  const { user, isLoading } = useGlobalContext()

  // Check any match in the chain — with nested routes, isShared is on the parent handle
  const isShared = matches.some((m) => m.handle?.isShared)

  // Redirect unauthed users to login page if route is not shared (with redirect to desired page)
  useEffect(() => {
    if (!isLoading && !user && !isShared) {
      const returnTo = location.pathname + location.search
      navigate(`/auth/login?redirect=${encodeURIComponent(returnTo)}`, { replace: true })
    }
  }, [isLoading, user, isShared, location.pathname, location.search, navigate])

  if (!isShared && (isLoading || !user)) {
    return <LoadingScreen />
  }

  return <>{children}</>
}
